import { useTranslation } from "react-i18next"
import { Step } from "react-joyride"
import { CustomBodyText, CustomHeader } from "../manualComponets"
import { ScrollRule } from "../UserManual"

export function getcbmScrollRules(windowWidth: number): {
	target: string
	opts: ScrollRule
}[] {
	return [
		{
			target: "#header-user-manual",
			opts: {
				containerSelector: "#body-layout",
				x: {
					offset: -24,
					align: "end",
				},
				y: {
					offset: 10000,
					align: "end",
				},
			},
		},
		{
			target: "#component-group-select",
			opts: {
				containerSelector: "#body-layout",
				x: {
					align: "center",
				},
				y: {
					offset: 10000,
					align: "end",
				},
			},
		},
		{
			target: "#generate-report-button",
			opts: {
				containerSelector: "#body-layout",
				x: {
					offset: 24,
					align: "start",
				},
				y: {
					offset: 10000,
					align: "end",
				},
			},
		},
		{
			target: "#opModeSelect",
			opts: {
				containerSelector: "#body-layout",
				x: {
					offset: 24,
					align: "start",
				},
				y: {
					offset: 10000,
					align: "end",
				},
			},
		},
		{
			target: "#time-selection",
			opts: {
				containerSelector: "#body-layout",
				x: {
					offset: -24,
					align: "start",
				},
				y: {
					offset: 10000,
					align: "end",
				},
			},
		},
		{
			target: "#avg-load-running-hours-plot-container",
			opts: {
				containerSelector: "#body-layout",
				x: {
					offset: 24,
					align: "start",
				},
				y: { offset: -24, align: "end" },
			},
		},
		{
			target: "#time-in-load-thresholded-plot-container",
			opts: {
				containerSelector: "#body-layout",
				x: {
					offset: 24,
					align: "start",
				},
				y: { offset: -24, align: "end" },
			},
		},
		{
			target: "#load-thresholded-input",
			opts: {
				containerSelector: "#body-layout",
				x: {
					offset: 24,
					align: "start",
				},
				y: {
					offset: 10000,
					align: "end",
				},
			},
		},
		{
			target: "#cbm-component-load-card",
			opts: {
				containerSelector: "#body-layout",
				x: {
					offset: 24,
					align: "start",
				},
				y: { offset: 120, align: "start" },
			},
		},
		{
			target: "#cbm-performance-parameter-select",
			opts: {
				containerSelector: "#body-layout",
				x: {
					offset: 24,
					align: "start",
				},
				y: { offset: 120, align: "start" },
			},
		},
		{
			target: "#cbm-statistics-display",
			opts: {
				containerSelector: "#body-layout",
				x: {
					offset: 24,
					align: "start",
				},
				y: { offset: 120, align: "start" },
			},
		},
		{
			target: "#component-card-load-distribution-plot",
			opts: {
				containerSelector: "#body-layout",
				x: {
					offset: 24,
					align: "start",
				},
				y: { offset: 120, align: "start" },
			},
		},
		{
			target: "#load-distribution-csv-download-button",
			opts: {
				containerSelector: "#body-layout",
				x: {
					offset: 24,
					align: "start",
				},
				y: { offset: 125, align: "start" },
			},
		},
		{
			target: "#component-card-avg-load-and-performance-dev-plot-container",
			opts: {
				containerSelector: "#body-layout",
				x: {
					offset: 24,
					align: "start",
				},
				y:
					windowWidth < 1280
						? { offset: -24 - 45 - 14, align: "end" }
						: { offset: 120, align: "start" },
			},
		},
		{
			target: "#component-card-overhaul-interval-display",
			opts: {
				containerSelector: "#body-layout",
				x: {
					offset: 24,
					align: "start",
				},
				y: windowWidth < 1280 ? { offset: -24, align: "end" } : undefined,
			},
		},
	]
}

export const cbmSteps = (translationFunc?: ReturnType<typeof useTranslation>["t"]): Step[] => {
	const t = translationFunc ?? ((text: string) => text)
	return [
		{
			target: "#header-user-manual",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("cbm.pageHeader.header")}</CustomHeader>
					<CustomBodyText>{t("cbm.pageHeader.content")}</CustomBodyText>
				</div>
			),
			placement: "left",
			disableBeacon: true,
		},
		{
			target: "#component-group-select",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("cbm.componentGroupSelect.header")}</CustomHeader>
					<CustomBodyText>{t("cbm.componentGroupSelect.content")}</CustomBodyText>
				</div>
			),
		},
		{
			target: "#generate-report-button",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("cbm.generateReportButton.header")}</CustomHeader>
					<CustomBodyText>{t("cbm.generateReportButton.content")}</CustomBodyText>
				</div>
			),
			placement: "bottom-start",
		},
		{
			target: "#opModeSelect",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("cbm.opModeSelect.header")}</CustomHeader>
					<CustomBodyText>{t("cbm.opModeSelect.content")}</CustomBodyText>
				</div>
			),
			placement: "bottom-start",
		},
		{
			target: "#time-selection",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("cbm.timeSelection.header")}</CustomHeader>
					<CustomBodyText>{t("cbm.timeSelection.content")}</CustomBodyText>
				</div>
			),
			placement: "bottom-end",
		},
		{
			target: "#avg-load-running-hours-plot-container",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("cbm.avgLoadRunningHoursPlotContainer.header")}</CustomHeader>
					<CustomBodyText>
						{t("cbm.avgLoadRunningHoursPlotContainer.content")}
					</CustomBodyText>
				</div>
			),
			placement: "top",
		},
		{
			target: "#time-in-load-thresholded-plot-container",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>
						{t("cbm.timeInLoadThresholdedPlotContainer.header")}
					</CustomHeader>
					<CustomBodyText>
						{t("cbm.timeInLoadThresholdedPlotContainer.content")}
					</CustomBodyText>
				</div>
			),
			placement: "top",
		},
		{
			target: "#load-thresholded-input",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("cbm.loadThresholdedInput.header")}</CustomHeader>
					<CustomBodyText>{t("cbm.loadThresholdedInput.content")}</CustomBodyText>
				</div>
			),
			placement: "bottom-end",
		},
		{
			target: "#cbm-component-load-card",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("cbm.componentLoadCard.header")}</CustomHeader>
					<CustomBodyText>{t("cbm.componentLoadCard.content")}</CustomBodyText>
				</div>
			),
		},
		{
			target: "#cbm-performance-parameter-select",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("cbm.performanceParameterInput.header")}</CustomHeader>
					<CustomBodyText>{t("cbm.performanceParameterInput.content")}</CustomBodyText>
				</div>
			),
			placement: "right",
		},
		{
			target: "#cbm-statistics-display",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("cbm.statisticsDisplay.header")}</CustomHeader>
					<CustomBodyText>{t("cbm.statisticsDisplay.content")}</CustomBodyText>
				</div>
			),
			placement: "top",
		},
		{
			target: "#component-card-load-distribution-plot",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("cbm.loadDistributionPlot.header")}</CustomHeader>
					<CustomBodyText>{t("cbm.loadDistributionPlot.content")}</CustomBodyText>
				</div>
			),
			placement: "top",
		},
		{
			target: "#load-distribution-csv-download-button",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("cbm.loadDistributionCsvDownloadButton.header")}</CustomHeader>
					<CustomBodyText>
						{t("cbm.loadDistributionCsvDownloadButton.content")}
					</CustomBodyText>
				</div>
			),
			placement: "bottom-end",
		},
		{
			target: "#component-card-avg-load-and-performance-dev-plot-container",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("cbm.avgLoadAndPerformanceDevPlot.header")}</CustomHeader>
					<CustomBodyText>{t("cbm.avgLoadAndPerformanceDevPlot.content")}</CustomBodyText>
				</div>
			),
			placement: "top",
		},
		{
			target: "#component-card-overhaul-interval-display",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("cbm.overhaulIntervalDisplay.header")}</CustomHeader>
					<CustomBodyText>{t("cbm.overhaulIntervalDisplay.content")}</CustomBodyText>
				</div>
			),
			placement: "top",
		},
	]
}
